import { initialsFromName } from '@/utils/initials'
import { cn } from '@/utils/cn'
import { useAuthSession } from '@/hooks/useAuth'
import { useAppRoute } from '@/hooks/useAppRoute'

type NavItem = {
  id: 'agencies' | 'audit' | 'settings'
  code: string
  label: string
  path?: '/agencies'
}

const NAV_ITEMS: NavItem[] = [
  { id: 'agencies', code: 'AG', label: 'Agencies', path: '/agencies' },
  { id: 'audit', code: 'AU', label: 'Audit log' },
  { id: 'settings', code: 'ST', label: 'Settings' },
]

export function Sidebar() {
  const { displayName, logout } = useAuthSession()
  const { isAgenciesRoute, navigateToAgencies } = useAppRoute()

  return (
    <aside
      aria-label="Superadmin navigation"
      className={cn(
        'flex min-h-screen flex-col gap-6 border-r border-white/10 bg-sidebar px-4 py-5 text-sidebar-text',
        'bg-[linear-gradient(180deg,rgba(255,255,255,0.04),transparent_28%)]',
      )}
    >
      <div className="px-2.5 pt-1">
        <p className="mb-2.5 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-sidebar-muted">
          Superadmin
        </p>
        <h1 className="font-display text-[1.55rem] font-medium leading-tight tracking-[-0.02em] text-white">
          Onn Record
        </h1>
        <p className="mt-1.5 text-[0.82rem] text-sidebar-muted">
          Internal · Agency accounts
        </p>
      </div>

      <nav aria-label="Primary" className="flex flex-1 flex-col gap-1">
        {NAV_ITEMS.map((item) => {
          const isActive = item.id === 'agencies' ? isAgenciesRoute : false
          const isDisabled = !item.path

          return (
            <button
              key={item.id}
              type="button"
              disabled={isDisabled}
              aria-disabled={isDisabled || undefined}
              aria-current={isActive ? 'page' : undefined}
              onClick={item.path ? () => navigateToAgencies() : undefined}
              className={cn(
                'grid w-full grid-cols-[2rem_1fr_auto] items-center gap-2.5 rounded-none px-2.5 py-2.5 text-left transition-colors',
                isActive
                  ? 'bg-white/10 text-white'
                  : 'text-sidebar-text hover:bg-white/5',
                isDisabled && 'cursor-not-allowed opacity-60',
              )}
            >
              <span
                className={cn(
                  'font-mono text-[0.68rem] tracking-[0.08em]',
                  isActive ? 'text-white' : 'text-sidebar-muted',
                )}
              >
                {item.code}
              </span>
              <span className="text-[0.92rem] font-semibold">{item.label}</span>
            </button>
          )
        })}
      </nav>

      <div className="flex flex-col gap-3 border-t border-white/10 px-2.5 pt-3">
        <div className="flex items-center gap-2.5">
          <div
            aria-hidden="true"
            className="grid h-9 w-9 place-items-center rounded-full bg-[linear-gradient(145deg,#3a4258,#1b2030)] text-[0.72rem] font-bold tracking-[0.04em] text-white"
          >
            {initialsFromName(displayName)}
          </div>
          <div>
            <p className="text-[0.88rem] font-semibold text-white">{displayName}</p>
            <p className="mt-0.5 text-[0.75rem] text-sidebar-muted">Platform</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void logout()}
          className="w-full border border-white/15 px-3 py-2 text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-sidebar-text transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Sign out
        </button>
      </div>
    </aside>
  )
}
